'use client';

import { useQuery } from '@apollo/client';
import { Alert, Button, Card, CardContent, Container, Fade, Link, Skeleton, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { BoxArrowUpRight, Download, Unlock } from 'react-bootstrap-icons';
import { serializeError } from 'serialize-error';
import CompletionScoreCard from '@/components/CompletionScoreCard';
import DataCollection from '@/components/DataCollection';
import { HelpText } from '@/components/HelpText';
import IntroSection from '@/components/IntroSection';
import { useSnackbar } from '@/components/SnackbarProvider';
import { ImportExportActions } from '@/components/import-export/ImportExportActions';
import { SUPPORT_FORM_URL } from '@/components/links';
import { usePlans } from '@/components/providers/SelectedPlanProvider';
import { benefits } from '@/constants/intro-content';
import { usePermissions } from '@/hooks/use-user-profile';
import { useSetInstanceLocked } from '@/queries/framework/lock-framework-config';
import { GET_MEASURE_TEMPLATES } from '@/queries/get-measure-templates';
import type { GetMeasureTemplatesQuery, GetMeasureTemplatesQueryVariables } from '@/types/__generated__/graphql';
import Loading, { LoadingCard } from '../loading';
function CompletionScoreCardWrapper({
  instance
}: {
  instance: string;
}) {
  const {
    data,
    error,
    loading
  } = useQuery<GetMeasureTemplatesQuery, GetMeasureTemplatesQueryVariables>(GET_MEASURE_TEMPLATES, {
    variables: {
      frameworkConfigId: instance
    }
  });
  if (loading) {
    return <Skeleton height={100} />;
  }
  if (!data?.framework || error) {
    if (error) {
      Sentry.captureException(error, {
        extra: {
          location: 'CompletionScoreCardWrapper',
          error: JSON.stringify(serializeError(error), null, 2)
        }
      });
    }
    return <h1>Something went wrong</h1>;
  }
  return <Fade in data-sentry-element="Fade" data-sentry-component="CompletionScoreCardWrapper" data-sentry-source-file="page.tsx">
      <div>
        <CompletionScoreCard measureTemplates={data.framework} data-sentry-element="CompletionScoreCard" data-sentry-source-file="page.tsx" />
      </div>
    </Fade>;
}
function DataCollectionContent({
  instance
}: {
  instance: string;
}) {
  const {
    data,
    error,
    loading
  } = useQuery<GetMeasureTemplatesQuery, GetMeasureTemplatesQueryVariables>(GET_MEASURE_TEMPLATES, {
    variables: {
      frameworkConfigId: instance
    }
  });
  const permissions = usePermissions();
  if (loading) {
    return <LoadingCard />;
  }
  if (!data || error) {
    if (error) {
      Sentry.captureException(error, {
        extra: {
          location: 'DataCollectionContent',
          error: JSON.stringify(serializeError(error), null, 2)
        }
      });
    }
    return <h1>Something went wrong</h1>;
  }
  return <Fade in data-sentry-element="Fade" data-sentry-component="DataCollectionContent" data-sentry-source-file="page.tsx">
      <div>
        <Stack direction="row" alignItems="center" justifyContent="space-between" data-sentry-element="Stack" data-sentry-source-file="page.tsx">
          <Typography gutterBottom variant="h3" component="h2" data-sentry-element="Typography" data-sentry-source-file="page.tsx">
            Data collection center
          </Typography>
          {!!data.framework && permissions.edit && !permissions.isLocked && <Stack direction="row" spacing={2}>
              <ImportExportActions measureTemplates={data.framework} />
            </Stack>}
        </Stack>
        {!!data.framework && <DataCollection measureTemplates={data.framework} />}
      </div>
    </Fade>;
}
export default function DashboardContent() {
  const {
    allPlans,
    selectedPlan
  } = usePlans();
  const permissions = usePermissions();
  const {
    setNotification
  } = useSnackbar();
  const [unlockPlan, {
    loading: unlocking
  }] = useSetInstanceLocked(selectedPlan?.id);
  async function handleUnlock() {
    if (!selectedPlan || !selectedPlan.instanceIdentifier) {
      return;
    }
    try {
      await unlockPlan({
        variables: {
          instanceId: selectedPlan.instanceIdentifier,
          isLocked: false
        }
      });
      setNotification({
        message: `"${selectedPlan.organizationName}" unlocked. Editing has been enabled.`,
        severity: 'success'
      });
    } catch {
      setNotification({
        message: 'Failed to unlock the plan. Please try again.',
        severity: 'error'
      });
    }
  }
  if (allPlans === null) {
    return <Loading />;
  }
  if (permissions.isLoading) {
    return <Container>
        <Stack spacing={4}>
          <Skeleton variant="rounded" height={150} />
          <LoadingCard />
        </Stack>
      </Container>;
  }
  if (allPlans.length === 0) {
    return <Fade in>
        <Container>
          <Stack spacing={6}>
            <Card>
              <CardContent>
                <Typography variant="h2" paragraph>
                  Welcome to NetZeroPlanner
                </Typography>
                <Typography variant="subtitle1" sx={{
                maxWidth: '80%'
              }}>
                  It looks like there aren't any plans for your city yet.{' '}
                  {permissions.isLoading ? <Skeleton variant="text" width="60%" sx={{
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }} /> : !permissions.create ? <>
                      You don't currently have permission to create one. To request edit access,
                      please fill out{' '}
                      <Link target="_blank" rel="noopener noreferrer" href={SUPPORT_FORM_URL}>
                        this form
                      </Link>
                      .
                    </> : 'Get started by creating a new plan to begin your decarbonisation journey.'}
                </Typography>
              </CardContent>
            </Card>
            <IntroSection title="How NetZeroPlanner guides your city's decarbonisation journey" items={benefits} />
          </Stack>
        </Container>
      </Fade>;
  }
  return <Fade in data-sentry-element="Fade" data-sentry-component="DashboardContent" data-sentry-source-file="page.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="page.tsx">
        <Stack spacing={4} data-sentry-element="Stack" data-sentry-source-file="page.tsx">
          {!selectedPlan ? <Skeleton variant="rounded" width="100%">
              <Card>
                <CardContent>
                  <Typography variant="h1">Placeholder</Typography>
                  <Typography>Placeholder</Typography>
                </CardContent>
              </Card>
            </Skeleton> : <Card>
              {selectedPlan.isLocked && <Alert severity="warning" sx={theme => ({
            borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
            borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          })} action={permissions.isAdmin ? <Button color="inherit" size="small" startIcon={<Unlock size={14} />} onClick={() => void handleUnlock()} disabled={unlocking}>
                        Unlock plan
                      </Button> : undefined}>
                  <strong>This plan is locked.</strong> Data entry and edits are disabled for all
                  users until an admin unlocks it.
                </Alert>}

              <CardContent>
                <Stack spacing={2}>
                  <div>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="h1">{selectedPlan.organizationName}</Typography>
                    </Stack>
                  </div>

                  {selectedPlan ? <CompletionScoreCardWrapper instance={selectedPlan.id} /> : <Skeleton />}

                  <Stack direction="row" spacing={2}>
                    {!!selectedPlan.viewUrl && <Button variant="outlined" rel="noopener noreferrer" target="_blank" endIcon={<BoxArrowUpRight size={16} />} href={selectedPlan.viewUrl}>
                        View Outcomes Dashboard
                      </Button>}

                    {selectedPlan.resultsDownloadUrl && <Button variant="outlined" endIcon={<Download size={18} />} href={selectedPlan.resultsDownloadUrl} download>
                        Export Outcomes as Excel{' '}
                        <HelpText text={<>
                              Here you can download tables that are helpful in building an Economic
                              Case for your Climate Action Plan. These tables display an analysis of
                              GHG reduction along with the costs and benefits by sub-sector and
                              stakeholder group. This will provide you with both the GHG and
                              financial Return on Investment for better decision making in your
                              Climate Action Plan.
                            </>} />
                      </Button>}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>}

          {selectedPlan ? <DataCollectionContent instance={selectedPlan.id} /> : <LoadingCard />}
        </Stack>
      </Container>
    </Fade>;
}